import { IntegrationEnum } from 'models/InternalIntegrationSetting';
import { Navigate, useParams } from 'react-router-dom';
import useFetchIntegrationAgencySettings from 'hooks/useFetchIntegrationAgencySettings';
import Loader from 'components/loader/Loader';
import { useTranslation } from 'react-i18next';
import { Glyphicon } from 'react-bootstrap';
import Tooltip from 'components/tooltip/Tooltip';
import useFeatures from 'hooks/useFeatures';
import Breadcrumbs from '../components/breadcrumbs/Breadcrumbs';
import useShouldRedirectToIntegrationsList from './useShouldRedirectToIntegrationsList';
import {
  ButtonStyled,
  ContainerStyled,
  DescriptionStyled,
  TitleStyled,
  IntegrationConnectionWrapper,
  PaymentWarningContainerStyled,
} from './IntegrationConnection.styles';
import useRedirectURL from './useRedirectURL';

const IntegrationConnection = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { allowIntegrationsV3 } = useFeatures();
  const {
    data: integrationAgencySettings,
    isLoading: isLoadingIntegrationAgencySettings,
  } = useFetchIntegrationAgencySettings({
    enumId: id as IntegrationEnum,
  });

  const { data: redirectURL, isLoading: isLoadingRedirectURL } = useRedirectURL(
    {
      integrationEnum: id as IntegrationEnum,
      enabled: !!id,
    },
  );

  const isLoading = isLoadingIntegrationAgencySettings || isLoadingRedirectURL;

  const integrationName =
    integrationAgencySettings?.integration?.displayName ??
    integrationAgencySettings?.integration?.name;

  const shouldShowPaymentWarning = [
    IntegrationEnum.STRIPE,
    IntegrationEnum.PAYPAL,
  ].includes(id as IntegrationEnum);

  const shouldRedirectToIntegrationsList = useShouldRedirectToIntegrationsList(
    id as IntegrationEnum,
  );

  if (!allowIntegrationsV3) {
    return <Navigate to="/integrations-migrated" />;
  }

  if (shouldRedirectToIntegrationsList) {
    return <Navigate to="/integrations" />;
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
    <IntegrationConnectionWrapper>
      <Breadcrumbs
        name={integrationName ?? ''}
        logo={integrationAgencySettings?.integration?.logo ?? ''}
      />

      <ContainerStyled>
        <TitleStyled data-testid="integration-connection-title">
          {t('pageIntegrations.integrationConnection.title', {
            integration: integrationName,
          })}
        </TitleStyled>

        {shouldShowPaymentWarning && (
          <PaymentWarningContainerStyled data-testid="payment-warning-container">
            {t('pageIntegrations.integrationConnection.paymentWarning')}

            <Tooltip
              text={t(
                `pageIntegrations.integrationConnection.paymentFeeWarningTooltip`,
                {
                  integration: integrationName,
                },
              )}
              id="payment-warning-tooltip"
            >
              <Glyphicon
                glyph="info-sign"
                data-testid="payment-warning-tooltip"
              />
            </Tooltip>
          </PaymentWarningContainerStyled>
        )}

        <DescriptionStyled data-testid="integration-connection-description">
          {t('pageIntegrations.integrationConnection.description', {
            integration: integrationName,
          })}
        </DescriptionStyled>

        <ButtonStyled
          data-testid="integration-connection-button"
          href={redirectURL}
          bsStyle="primary"
          rel="noreferrer"
          target="_self"
          disabled={!redirectURL}
        >
          {t('pageIntegrations.integrationConnection.connect')}
        </ButtonStyled>
      </ContainerStyled>
    </IntegrationConnectionWrapper>
  );
};

export default IntegrationConnection;
